import { dump as yamlDump } from 'js-yaml';
import { analyzeTopology } from './analyzer/topology';
import { validateFlowGraph } from './analyzer/validator';
import { YamlParser } from './parser/YamlParser';
import { NativeStrategy } from './strategies/native';
import { StateMachineStrategy } from './strategies/state-machine';
import { computeNodeVisitOrder } from './utils/nodeVisitOrder';
/**
 * Main transpiler class for converting React Flow graphs to Home Assistant YAML
 */
export class FlowTranspiler {
    strategies = [new NativeStrategy(), new StateMachineStrategy()];
    /**
     * Validate a flow graph input
     */
    validate(input) {
        return validateFlowGraph(input);
    }
    /**
     * Analyze the topology of a validated flow graph
     */
    analyzeTopology(flow) {
        return analyzeTopology(flow);
    }
    /**
     * Transpile a flow graph to Home Assistant YAML
     */
    transpile(input, options = {}) {
        const warnings = [];
        // Step 1: Validate the input
        const validation = this.validate(input);
        if (!validation.success || !validation.graph) {
            return {
                success: false,
                errors: validation.errors.map((e) => e.message),
                warnings,
            };
        }
        const flow = validation.graph;
        // Step 2: Analyze topology
        const analysis = this.analyzeTopology(flow);
        // Step 3: Select strategy
        let strategy;
        if (options.forceStrategy) {
            const forced = this.strategies.find((s) => s.name === options.forceStrategy);
            if (!forced) {
                return {
                    success: false,
                    errors: [`Unknown strategy: ${options.forceStrategy}`],
                    warnings,
                };
            }
            strategy = forced;
            if (!strategy.canHandle(analysis)) {
                warnings.push(`Strategy "${strategy.name}" may not be optimal for this flow topology. ` +
                    `Recommended: ${analysis.recommendedStrategy}`);
            }
        }
        else {
            // Auto-select based on topology
            const suitable = this.strategies.find((s) => s.canHandle(analysis));
            if (!suitable) {
                // Fall back to state-machine which handles everything
                strategy = new StateMachineStrategy();
            }
            else {
                strategy = suitable;
            }
        }
        // Step 4: Generate YAML output
        const output = strategy.generate(flow, analysis);
        warnings.push(...output.warnings);
        // Step 5: Inject _cafe_metadata metadata with node positions
        const yamlContent = output.automation ?? output.script;
        let yaml;
        if (yamlContent && typeof yamlContent === 'object') {
            const metadata = this.generateCafeMetadata(flow, strategy);
            const contentWithMetadata = {
                ...yamlContent,
                variables: {
                    // First, include user-defined variables from the flow graph
                    ...(flow.userVariables || {}),
                    // Then include any variables from the generated YAML (e.g., state machine vars)
                    ...(yamlContent.variables || {}),
                    // Finally, add _cafe_metadata
                    _cafe_metadata: metadata,
                },
            };
            // Step 6: Serialize to YAML string with metadata
            yaml = yamlDump(contentWithMetadata, {
                indent: options.indent ?? 2,
                lineWidth: options.lineWidth ?? -1,
                quotingType: '"',
                forceQuotes: false,
            });
        }
        else {
            // Serialize without metadata
            yaml = yamlDump(yamlContent, {
                indent: options.indent ?? 2,
                lineWidth: options.lineWidth ?? -1,
                quotingType: '"',
                forceQuotes: false,
            });
        }
        return {
            success: true,
            yaml,
            output,
            analysis,
            warnings,
        };
    }
    /**
     * Transpile to YAML string directly
     */
    toYaml(input, options = {}) {
        const result = this.transpile(input, options);
        if (!result.success) {
            throw new Error(`Transpilation failed: ${result.errors?.join(', ')}`);
        }
        return result.yaml;
    }
    /**
     * Force native strategy (for tree-shaped flows)
     */
    toNativeYaml(input, options = {}) {
        return this.toYaml(input, { ...options, forceStrategy: 'native' });
    }
    /**
     * Force state machine strategy (for complex flows)
     */
    toStateMachineYaml(input, options = {}) {
        return this.toYaml(input, { ...options, forceStrategy: 'state-machine' });
    }
    /**
     * Parse Home Assistant YAML back into FlowGraph
     */
    fromYaml(yamlString) {
        const parser = new YamlParser();
        return parser.parse(yamlString);
    }
    /**
     * Get available strategies
     */
    getStrategies() {
        return this.strategies.map((s) => ({
            name: s.name,
            description: s.description,
        }));
    }
    /**
     * Add a custom strategy
     */
    addStrategy(strategy) {
        this.strategies.unshift(strategy); // Add at beginning for priority
    }
    /**
     * Generate C.A.F.E. metadata for position persistence
     *
     * Note: We only store node positions in metadata. Node data and edges are already
     * encoded in the YAML structure itself:
     * - Node data is in each choose block's sequence
     * - Edges are in the variables transitions (current_node assignments)
     * - Node IDs are in the choose block conditions
     */
    generateCafeMetadata(flow, strategy) {
        const nodePositions = {};
        // Write positions in the same order YamlParser will re-assign IDs to
        // nodes on import (see computeNodeVisitOrder), not `flow.nodes` array
        // order — otherwise positions get attached to the wrong node once a
        // flow has multiple same-type nodes across parallel/condition branches.
        const nodesById = new Map(flow.nodes.map((node) => [node.id, node]));
        for (const nodeId of computeNodeVisitOrder(flow)) {
            const node = nodesById.get(nodeId);
            if (!node)
                continue;
            nodePositions[node.id] = {
                x: node.position.x,
                y: node.position.y,
            };
        }
        return {
            version: 1,
            nodes: nodePositions,
            graph_id: flow.id,
            graph_version: flow.version,
            strategy: strategy.name,
        };
    }
}
// Export singleton instance
export const transpiler = new FlowTranspiler();
//# sourceMappingURL=FlowTranspiler.js.map